import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import styles from './merchantList.module.scss';
import i18n from 'i18next';
import { RecentGroupDropdown } from 'components/RecentGroupDropdown/RecentGroupDropdown';
import {
  SessionStorageHelper,
  SessionStorageItemKeys
} from 'helper/StorageHelper';
import { StickableTanstackTable } from 'components/common/Table/StickableTanstackTable';
import { useCallAPI } from 'hooks/useCallAPI';
import { SortOrder } from 'components/common/Table/types';
import {
  AdvertiserManager,
  DefaultAdvertiserManager
} from 'core/advertiser/AdvertiserManager';
import { MerchantsWithPagination } from 'ws/AdvertiserWebService';
import { Pagination } from 'core/pagination/Pagination';
import { debounce } from 'lodash';
import { useCoreContext } from 'contexts/coreContext';
import { AgencyManager, DefaultAgencyManager } from 'core';
import {
  getDefaultColumnDescription,
  getLabelValueColumn,
  getNameIdColumn
} from 'components/TableColumn/TableColumn';
import { Page } from 'components/Page/Page';
import { isBeaconAdmin } from 'helper/ActorHelper';
import { LineBeaconServiceType } from 'core/advertiser/LineBeacon';
import { Merchant } from 'core/advertiser/Advertiser';

enum MERCHANT_COLUMNS {
  NAMEID = 'id',
  OFFICAIL_ACCOUNT = 'beaconConnectionName',
  WEBHOOK_TYPE = 'webhookType',
  HARDWARE_ID = 'hardwareId',
  OTHER_INFO = 'otherInfo'
}

const advertiserManager: AdvertiserManager = new DefaultAdvertiserManager();
const agencyManager: AgencyManager = new DefaultAgencyManager();
export const MerchantList: React.FC = () => {
  const { loading, callAPIs } = useCallAPI();
  const [agencies, setAgencies] = useState<SelectOptions[]>();
  const [sortField, setSortField] = useState<string>(MERCHANT_COLUMNS.NAMEID);
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [merchantsWithPagination, setMerchantsWithPagination] =
    useState<MerchantsWithPagination>();
  const [pagination, setPagination] = useState<Pagination>();
  const [searchString, setSearchString] = useState<string>('');

  const core = useCoreContext();
  const actor = core?.authenticationManager.actor;
  const isBeaconAdminActor = actor ? isBeaconAdmin(actor) : false;
  const defaultAgencyId = isBeaconAdminActor
    ? SessionStorageHelper.getNumberItem(SessionStorageItemKeys.AGENCY)
    : actor?.agencyId === null
      ? undefined
      : actor?.agencyId;
  const [agencyId, setAgencyId] = useState<number | undefined>(defaultAgencyId);

  const init = useCallback(
    (
      page: number | undefined = 1,
      overrides?: {
        searchString?: string;
        sortField?: string;
        sortOrder?: SortOrder;
      }
    ) => {
      const effectiveSortField = overrides?.sortField ?? sortField;
      const effectiveSortOrder = overrides?.sortOrder ?? sortOrder;
      const effectiveSearchString = overrides?.searchString ?? searchString;
      const sortFieldMap: Partial<Record<MERCHANT_COLUMNS, string>> = {
        [MERCHANT_COLUMNS.NAMEID]: 'advertiserId',
        [MERCHANT_COLUMNS.OFFICAIL_ACCOUNT]: 'name'
      };
      const sort =
        sortFieldMap[effectiveSortField as MERCHANT_COLUMNS] ??
        effectiveSortField;
      const pageable = {
        page,
        sizePerPage: 10,
        sort,
        direction: effectiveSortOrder
      };
      callAPIs(
        [
          () =>
            advertiserManager.getMerchants(
              agencyId,
              pageable,
              effectiveSearchString
            )
        ],
        (merchantsWithPagination) => {
          setMerchantsWithPagination(merchantsWithPagination);
          setPagination(merchantsWithPagination.pagination);
        }
      );
    },
    [agencyId, sortField, sortOrder, searchString, callAPIs]
  );

  const initRef = useRef(init);
  initRef.current = init;

  useEffect(() => {
    if (!isBeaconAdminActor) {
      callAPIs([() => initRef.current()]);
      return;
    }
    callAPIs(
      [() => agencyManager.getAgenciesOptions(), () => initRef.current()],
      setAgencies
    );
  }, [isBeaconAdminActor, callAPIs]);

  useEffect(() => {
    initRef.current();
  }, [agencyId]);

  const debouncedInit = useMemo(
    () =>
      debounce(
        (overrides?: {
          searchString?: string;
          sortField?: string;
          sortOrder?: SortOrder;
        }) => init(1, overrides),
        1000
      ),
    [init]
  );

  const sortDescriptor = useMemo(() => {
    return [
      {
        dataField: sortField,
        order: sortOrder
      }
    ] as [
      {
        dataField: any;
        order: SortOrder;
      }
    ];
  }, [sortField, sortOrder]);

  const sortMerchants = useCallback(
    (field: string, direction: SortOrder) => {
      const currentSortSetting = sortDescriptor[0];
      if (
        field === currentSortSetting.dataField &&
        direction === currentSortSetting.order
      ) {
        return;
      }
      setSortField(field);
      setSortOrder(direction);
      init(1, { sortField: field, sortOrder: direction });
    },
    [sortDescriptor, init]
  );

  const handleOnSearch = useCallback(
    (searchString: string) => {
      setSearchString(searchString);
      if (searchString === '') {
        debouncedInit && debouncedInit.cancel();
        init(1, { searchString });
      } else {
        debouncedInit({ searchString });
      }
    },
    [init, debouncedInit]
  );

  const viewModelData = useMemo(() => {
    if (!merchantsWithPagination) {
      return [];
    }
    return merchantsWithPagination.merchants.map((merchant) => {
      return {
        ...merchant,
        name: merchant.advertiserName,
        [MERCHANT_COLUMNS.NAMEID]: merchant.advertiserId,
        [MERCHANT_COLUMNS.OFFICAIL_ACCOUNT]: merchant.beaconConnectionName,
        [MERCHANT_COLUMNS.WEBHOOK_TYPE]: merchant.webhookType,
        [MERCHANT_COLUMNS.HARDWARE_ID]: merchant.hardwareId
      };
    });
  }, [merchantsWithPagination]);

  const handleTableChange = useCallback(
    (type, props) => {
      if (type === 'sort') {
        sortMerchants(props.sortField, props.sortOrder);
        return;
      }
      init(props.page);
    },
    [sortMerchants, init]
  );

  const columnSettings = useMemo(() => {
    return [
      getNameIdColumn(
        getDefaultColumnDescription(
          MERCHANT_COLUMNS.NAMEID,
          i18n.t('merchantList.headers.name')
        ),
        () => undefined
      ),
      getDefaultColumnDescription(
        MERCHANT_COLUMNS.OFFICAIL_ACCOUNT,
        i18n.t('merchantList.headers.officialAccount')
      ),
      {
        ...getDefaultColumnDescription(
          MERCHANT_COLUMNS.WEBHOOK_TYPE,
          i18n.t('beaconConnectionList.headers.webhookType')
        ),
        formatter: (value) =>
          value ? i18n.t(`beaconFields.labels.${value}`) : 'N/A'
      },
      {
        ...getDefaultColumnDescription(
          MERCHANT_COLUMNS.HARDWARE_ID,
          i18n.t('beaconConnectionList.headers.hardwareId')
        ),
        formatter: (value) => value || 'N/A'
      },
      getLabelValueColumn(
        {
          ...getDefaultColumnDescription(
            MERCHANT_COLUMNS.OTHER_INFO,
            i18n.t('beaconConnectionList.headers.otherInfo'),
            false
          ),
          classes: () => styles.otherInfo
        },
        (merchant: Merchant) => {
          console.log(merchant);
          if (!(merchant.webhookType === LineBeaconServiceType.TENMAX)) {
            return undefined;
          }
          return [
            {
              label: i18n.t('beaconFields.labels.channelSecret'),
              value: merchant.channelSecret || ''
            },
            {
              label: i18n.t('beaconFields.labels.channelAccessToken'),
              value: merchant.channelAccessToken || ''
            }
          ];
        },
        styles.otherInfoValue
      )
    ];
  }, []);

  const renderInTopArea = useCallback(() => {
    return agencies ? (
      <div className={styles.agencyOptions}>
        <div className={styles.title}>
          {i18n.t('merchantList.labels.agencyOptionsTitle')}
        </div>
        <RecentGroupDropdown
          value={agencyId}
          options={agencies}
          storageKey={SessionStorageItemKeys.AGENCY}
          callback={setAgencyId}
        />
      </div>
    ) : (
      <div />
    );
  }, [agencies, agencyId]);

  // const renderListOperations = useCallback(() => {
  //   const renderDiv = () => <div />;
  //   return [
  //     <PermissionChecker
  //       key='new-btn'
  //       permissionAware={isRoles(RoleNames.beaconAdmin)}
  //       renderOtherwise={renderDiv}
  //     >
  //       <Link to={'/merchants/new'} className='btn btn-tertiary btn-sm'>
  //         {i18n.t('merchantList.labels.newMerchant')}
  //       </Link>
  //     </PermissionChecker>
  //   ];
  // }, []);

  if (!merchantsWithPagination || !pagination) {
    return <LoadingIndicator />;
  }

  return (
    <div style={{ height: '100%' }}>
      {loading && <LoadingIndicator />}
      <Page
        title={i18n.t('merchantList.title')}
        renderInTopArea={renderInTopArea}
      >
        <StickableTanstackTable
          stickFirstColumn
          stickLastColumn
          customPadding={false}
          remote
          keyField={'id'}
          data={viewModelData}
          columns={columnSettings}
          pagination={{
            page: pagination.page,
            sizePerPage: 10,
            totalSize: pagination.totalCount
          }}
          defaultSorted={sortDescriptor}
          onTableChange={handleTableChange}
          onHandleSearch={handleOnSearch}
          noDataIndication={i18n.t('merchantList.labels.nodata')}
          // renderListOperations={renderListOperations}
          searchbarPlaceholder={i18n.t('merchantList.placeholers.searchBar')}
        />
      </Page>
    </div>
  );
};
